import React, { useState, useCallback } from "react";
import { Box, Grid, Typography } from "@mui/material";
import { FC } from "react";
import Image from "next/image";
import SimpleImageViewer from "react-simple-image-viewer";
import Xarrow from "react-xarrows";
import intro from "../../assets/intro.jpg";
import step1 from "../../assets/step 1.jpg";
import step2 from "../../assets/Step 2.jpg";
import step3 from "../../assets/Step 3.jpg";
import step4 from "../../assets/Step 4.jpg";
import step5 from "../../assets/Step 5.jpg";
import step6 from "../../assets/Step 6.jpg";
import hindiIntro from "../../assets/intro hindi.jpg";
import hindiStep1 from "../../assets/step-1-hindi.jpg";
import hindiStep2 from "../../assets/Step-2-hindi.jpg";
import hindiStep3 from "../../assets/Step-3-hindi.jpg";
import hindiStep4 from "../../assets/Step-4-hindi.jpg";
import hindiStep5 from "../../assets/Step-5-hindi.jpg";
import hindiStep6 from "../../assets/Step-6-hindi.jpg";
import "./index.css";

interface JoinStepsProps {
  selected: any;
}

export const JoinSteps: FC<JoinStepsProps> = ({ selected }) => {
  const images = selected
    ? [intro, step1, step2, step3, step4, step5, step6]
    : [
        hindiIntro,
        hindiStep1,
        hindiStep2,
        hindiStep3,
        hindiStep4,
        hindiStep5,
        hindiStep6,
      ];

  const [currentImage, setCurrentImage] = useState(0);
  const [isViewerOpen, setIsViewerOpen] = useState(false);

  const openImageViewer = useCallback((index: any) => {
    setCurrentImage(index);
    setIsViewerOpen(true);
  }, []);

  const closeImageViewer = () => {
    setCurrentImage(0);
    setIsViewerOpen(false);
  };

  return (
    <Box
      sx={{
        margin: { xs: "1rem auto 1rem", sm: "1rem auto 2rem" },
        maxWidth: "100%",
      }}
    >
      <Grid
        container
        sx={{
          width: "100%",
          maxWidth: "min(92%, 1220px)",
          margin: "0 auto",
          px: { xs: 2, sm: 3 },
        }}
      >
        <Typography
          variant="h4"
          component="h2"
          className="join-professional-steps"
          sx={{
            fontWeight: 700,
            padding: { xs: "1rem 0", sm: "1.5rem 0" },
            fontSize: { xs: "1.25rem", sm: "1.75rem" },
            color: "#123524",
          }}
        >
          {selected ? (
            <>
              6 easy steps to join{" "}
              <Box component="span" sx={{ color: "#1f7a4d" }}>
                Salon
              </Box>
              <Box component="span" sx={{ color: "#c9a227" }}>
                Tym
              </Box>
            </>
          ) : (
            <>
              <Box component="span" sx={{ color: "#1f7a4d" }}>
                Salon
              </Box>
              <Box component="span" sx={{ color: "#c9a227" }}>
                Tym
              </Box>{" "}
              से जुड़ने के 6 आसान चरण
            </>
          )}
        </Typography>
      </Grid>
      <Box
        display="flex"
        sx={{
          overflowX: "auto",
          overflowY: "hidden",
          gap: 4,
          padding: "10px",
          width: "100%",
          justifyContent: "flex-start",
          whiteSpace: "nowrap",
          position: "relative",
          "&::-webkit-scrollbar": {
            display: "none",
          },
        }}
      >
        {images.map((item, index) => (
          <Box
            key={index}
            id={`step-${index}`}
            position="relative"
            sx={{ display: "inline-block", cursor: "pointer" }}
            onClick={() => openImageViewer(index)}
          >
            <Image
              src={item}
              alt={`Step ${index + 1}`}
              width={2000}
              height={2000}
              style={{
                borderRadius: "8px",
                width: "20rem",
                maxWidth: "20rem",
                height: "auto",
              }}
            />
          </Box>
        ))}
        {images.map((_, index) => {
          if (index < images.length - 1) {
            return (
              <Xarrow
                key={`arrow-${index}`}
                start={`step-${index}`} // ID of the starting step
                end={`step-${index + 1}`} // ID of the next step
                color="gray"
                strokeWidth={2}
                path="straight"
                dashness
                headSize={4}
                startAnchor="right"
                endAnchor="left"
                zIndex={10}
              />
            );
          }
          return null;
        })}
      </Box>

      {/* Image Viewer with custom styles */}
      {isViewerOpen && images.length > 0 && (
        <Box
          sx={{
            zIndex: 9999,
            opacity: 1,
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
          }}
        >
          <SimpleImageViewer
            src={images.map((image) => image.src)}
            currentIndex={currentImage}
            disableScroll={true}
            closeOnClickOutside={true}
            onClose={closeImageViewer}
          />
        </Box>
      )}
      <Grid
        container
        sx={{
          maxWidth: "min(92%, 1220px)",
          margin: "2rem auto",
          px: { xs: 2, sm: 3 },
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            background:
              "linear-gradient(135deg, rgba(223, 246, 232, 0.95) 0%, rgba(246, 251, 248, 0.98) 100%)",
            padding: { xs: "1rem 1.25rem", sm: "1.25rem 1.5rem" },
            borderRadius: "14px",
            textAlign: "center",
            border: "1px solid rgba(18, 53, 36, 0.12)",
            boxShadow: "0 8px 28px rgba(18, 53, 36, 0.08)",
            width: "100%",
          }}
        >
          <Typography
            variant="body1"
            sx={{
              textAlign: "center",
              fontWeight: 600,
              color: "#123524",
              lineHeight: 1.6,
            }}
          >
            {selected
              ? "For any queries, get in touch with us at"
              : "किसी भी सवाल के लिए हमसे संपर्क करें:"}
            <br />
            <Box
              component="a"
              href="tel:+918510000223"
              sx={{
                fontWeight: 700,
                color: "#1f7a4d",
                textDecoration: "none",
                borderBottom: "2px solid rgba(31, 122, 77, 0.35)",
                display: "inline-block",
                mt: 0.5,
                "&:hover": { color: "#123524" },
              }}
            >
              +91 85100 00223
            </Box>
          </Typography>
        </Box>
      </Grid>
    </Box>
  );
};
